import { MenuPopover, MenuItems, MenuItem as MenuItem$1, MenuLink as MenuLink$1, MenuButton as MenuButton$1, Menu as Menu$1 } from '@reach/menu-button';
import React__default, { forwardRef } from 'react';
import styled from 'styled-components';
import { setTestId } from '../../lib/testId.js';
import { useLinkComponent } from '../../provider/Provider.js';
import { useQueryChildComponents } from '../base/SystemComponent.js';
import { Button } from '../button/Button.js';
import { ButtonIcon } from '../button/ButtonIcon.js';
import { COLOR_PALETTE } from '../colors/colorPalette.js';
import { Icon } from '../icon/Icon.js';
import { CSSDefaultPopover, CSSDefaultOption } from '../inputs/InputCommon.js';
import { Box } from '../layout/Box.js';
import { CSSOpacityHex } from '../styles/cssGenerate.js';
import { getThemeVariantContentColor } from '../styles/stylePropTypes.js';
import { Text } from '../text/Text.js';
import { Tooltip } from '../tooltip/Tooltip.js';

const StyledMenuPopover = styled(MenuPopover)`
  &[data-reach-menu-popover] {
    ${CSSDefaultPopover}
  }
`;
const StyledMenuItems = styled(MenuItems)`
  &[data-reach-menu-items] {
    ${CSSDefaultPopover}
    font-size: 100%;
    border: none;
    outline: none;
    padding: 0;
  }
`;
const StyledText = styled(Text)`
  color: ${COLOR_PALETTE.slateGrey80};
  text-transform: uppercase;
`;
const StyledMenuItem = styled(MenuItem$1)`
  &[data-reach-menu-item] {
    ${CSSDefaultOption}
    padding-right: 3rem;

    &:hover,
    &[data-selected],
    &[data-hpy-web-menu-item-selected]:hover {
      background-color: ${getThemeVariantContentColor}${CSSOpacityHex(0.05)};
      color: ${getThemeVariantContentColor};
    }

    &[data-hpy-web-menu-item-selected] {
      background-color: transparent;
      color: ${getThemeVariantContentColor};
    }

    & [${Icon.dataId}] {
      position: absolute;
      right: 1rem;
      top: 0.7rem;
      display: none;
    }

    &[data-hpy-web-menu-item-selected] [${Icon.dataId}] {
      display: block;
    }
  }
`;
const StyledMenuLink = styled(MenuLink$1)`
  ${CSSDefaultOption}
  padding-right: 3rem;
  color: ${COLOR_PALETTE.slateGreyDark50};

  text-decoration: none;

  &:hover {
    background-color: ${getThemeVariantContentColor}${CSSOpacityHex(0.05)};
    color: ${getThemeVariantContentColor};
  }

  & [${Icon.dataId}] {
    position: absolute;
    right: 1rem;
    top: 0.7rem;
    display: none;
  }

  &[data-hpy-web-menu-link-selected] {
    background-color: transparent;
    color: ${getThemeVariantContentColor};
  }
`;
/**
 * Interactable `Button` element that opens `Menu`
 *
 * @example
 * <Menu>
 *  <MenuButton>Actions</MenuButton>
 *  <MenuItem onSelect={() => {}}>Do something</MenuItem>
 * </Menu>
 */ const MenuButton = forwardRef(({ as =Button , ...props }, ref)=>React__default.createElement(MenuButton$1, {
        ref: ref,
        "data-hpy-web-menu-button": true,
        "data-test-id": "MenuButton-" + props.testId,
        as: as,
        ...props
    }, props.children));
MenuButton.dataId = "data-hpy-web-menu-button";
MenuButton.displayName = "MenuButton";
/**
 * Interactable `ButtonIcon` element that opens `Menu`
 *
 * @example
 * <Menu>
 *  <MenuButtonIcon icon="mui-ellipses" />
 *  <MenuItem onSelect={() => {}}>Do something</MenuItem>
 * </Menu>
 */ const MenuButtonIcon = forwardRef((props, ref)=>React__default.createElement(MenuButton$1, {
        ref: ref,
        "data-hpy-web-menu-buttonicon": true,
        "data-test-id": "MenuButton-" + props.testId,
        as: ButtonIcon,
        size: props.size,
        ...props
    }, props.children));
MenuButtonIcon.dataId = "data-hpy-web-menu-buttonicon";
MenuButtonIcon.displayName = "MenuButtonIcon";
/**
 * A selectable item within a `Menu`
 *
 * @example
 * <Menu>
 *  <MenuButtonIcon icon="mui-ellipses" />
 *  <MenuItem onSelect={() => {}}>Do something</MenuItem>
 * </Menu>
 */ const MenuItem = ({ variant ="context-inverse" , isSelected , testId ="X" , children , ...props })=>{
    return React__default.createElement(StyledMenuItem, {
        "data-hpy-web-menu-item": true,
        "data-test-id": "MenuItem-" + testId,
        "aria-selected": isSelected,
        ...isSelected && {
            "data-hpy-web-menu-item-selected": ""
        },
        ...props,
        variant: variant
    }, children);
};
MenuItem.dataId = "data-hpy-web-menu-item";
MenuItem.displayName = "MenuItem";
const isAnchor = (externalLink, props)=>{
    return externalLink || typeof props?.to === "string" && props?.to.startsWith("http");
};
/**
 * A selectable link within a `Menu`
 *
 * @example
 * <Menu>
 *  <MenuButtonIcon icon="mui-ellipses" />
 *  <MenuLink onSelect={() => {}} to="Go somewhere"}>Do something</MenuLink>
 * </Menu>
 */ const MenuLink = ({ variant ="context-inverse" , externalLink , isSelected =false , testId ="X" , children , ...props })=>{
    let element = useLinkComponent();
    if (isAnchor(externalLink, props)) {
        props.href = props.to;
        props.target = "_blank";
        props.rel = "noreferrer";
        element = "a";
    }
    return React__default.createElement(StyledMenuLink, {
        "data-hpy-web-menu-link": true,
        "data-test-id": "Link-" + testId,
        variant: variant,
        as: element,
        ...isSelected && {
            "data-hpy-web-menu-link-selected": ""
        },
        ...props
    }, children);
};
MenuLink.dataId = "data-hpy-web-menu-link";
MenuLink.displayName = "MenuLink";
/**
 * A header within a `Menu`
 *
 * @example
 * <Menu>
 *  <MenuButtonIcon icon="mui-ellipses" />
 *  <MenuHeading>Heading</MenuHeading>
 * </Menu>
 */ const MenuHeading = ({ children , ...props })=>{
    return React__default.createElement(Box, {
        padding: [
            0.75,
            1
        ],
        "data-hpy-web-menu-heading": true
    }, React__default.createElement(StyledText, {
        weight: "bold",
        size: "small",
        ...props
    }, children));
};
MenuHeading.dataId = "data-hpy-web-menu-heading";
MenuHeading.displayName = "MenuHeading";
/**
 * Generic Menu
 * Used for a menu list of 'actionable' items - e.g. a `SideDrawer` action menu.
 *
 * @example
 * <Menu>
 *  <MenuButtonIcon icon="mui-ellipses" />
 *  <MenuItem onSelect={() => {}}>Do something</MenuItem>
 * </Menu>
 *
 */ const Menu = ({ testId ="X" , position , children , ...props })=>{
    const [buttonChildren, otherChildren] = useQueryChildComponents(children, [
        MenuButton,
        MenuButtonIcon,
        Tooltip
    ]);
    return React__default.createElement(Menu$1, {
        "data-hpy-web-menu": true,
        ...setTestId("Menu-" + testId),
        ...props
    }, (menu)=>React__default.createElement(React__default.Fragment, null, buttonChildren, React__default.createElement(StyledMenuPopover, {
            position: position,
            portal: true,
            ...setTestId("Menu-PopOver" + testId)
        }, React__default.createElement(StyledMenuItems, null, menu.isExpanded && otherChildren))));
};
Menu.dataId = "data-hpy-web-menu";
Menu.displayName = "Menu";

export { Menu, MenuButton, MenuButtonIcon, MenuHeading, MenuItem, MenuLink };
